import { List, EditButton, ShowButton, DeleteButton, TagField } from "@refinedev/antd";
import { useTable } from "@refinedev/core";
import { Table, Space, Typography, Tooltip } from "antd";
import { FileTextOutlined, TranslationOutlined, ThunderboltOutlined } from "@ant-design/icons";
import type { AITool } from "../../types/aiTools";

const { Text } = Typography;

const iconMap: Record<string, React.ReactNode> = {
  "assets/icons/summarizer.svg": <FileTextOutlined />,
  "assets/icons/translator.svg": <TranslationOutlined />,
};

export const AIToolsList = () => {
  const { tableQuery, currentPage, setCurrentPage, pageSize, setPageSize, setSorters } = useTable<AITool>({
    resource: "ai-tools",
    syncWithLocation: true,
  });

  return (
    <List title="Narzędzia AI">
      <Table<AITool> dataSource={tableQuery.data?.data} loading={tableQuery.isLoading} rowKey="id"
        pagination={{ current: currentPage, pageSize, total: tableQuery.data?.total }}
        onChange={(page, _filters, sorter) => {
          setCurrentPage(page.current ?? 1); setPageSize(page.pageSize ?? 10);
          const sort = Array.isArray(sorter) ? sorter[0] : sorter;
          setSorters(sort?.order ? [{ field: String(sort.field), order: sort.order === "ascend" ? "asc" : "desc" }] : []);
        }}>
        <Table.Column
          dataIndex="icon"
          title=""
          width={50}
          render={(icon: string) => (
            <div style={{ fontSize: 24, color: "#1F8081" }}>
              {iconMap[icon] || <ThunderboltOutlined />}
            </div>
          )}
        />
        <Table.Column
          dataIndex="id"
          title="ID"
          width={150}
          render={(value: string) => (
            <Text copyable={{ text: value }} style={{ fontFamily: "monospace", fontSize: 12 }}>
              {value.length > 15 ? `${value.slice(0, 15)}...` : value}
            </Text>
          )}
        />
        <Table.Column
          dataIndex="title"
          title="Tytuł"
          render={(value: string) => <Text strong>{value}</Text>}
        />
        <Table.Column
          dataIndex="description"
          title="Opis"
          ellipsis
          render={(value: string) => (
            <Tooltip title={value}>
              <Text ellipsis style={{ maxWidth: 200 }}>
                {value}
              </Text>
            </Tooltip>
          )}
        />
        <Table.Column
          dataIndex="category"
          title="Kategoria"
          width={120}
          render={(value: string, record: AITool) => (
            <TagField
              value={value}
              style={{
                backgroundColor: `${record.categoryColor}20`,
                color: record.categoryColor,
                border: `1px solid ${record.categoryColor}40`,
              }}
            />
          )}
        />
        <Table.Column
          dataIndex="tags"
          title="Tagi"
          width={180}
          render={(tags: string[]) => (
            <Space size={4} wrap>
              {tags?.slice(0, 3).map((tag) => (
                <TagField key={tag} value={tag} />
              ))}
              {tags?.length > 3 && <Text type="secondary">+{tags.length - 3}</Text>}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="minPlanRequired"
          title="Plan min."
          width={90}
          render={(value: number) => (
            <Text
              style={{
                color: value <= 1 ? "#52c41a" : value <= 2 ? "#faad14" : "#ff4d4f",
              }}
            >
              {value === 1 ? "Free" : value === 2 ? "Pro" : "Enterprise"}
            </Text>
          )}
        />
        <Table.Column
          dataIndex="usageCount"
          title="Użycia"
          width={90}
          sorter
        />
        <Table.Column
          title="Pola form."
          width={100}
          render={(_: unknown, record: AITool) => (
            <Text>{record.formTemplate?.fields?.length || 0}</Text>
          )}
        />
        <Table.Column
          title="Akcje"
          dataIndex="actions"
          fixed="right"
          width={140}
          render={(_: unknown, record: AITool) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} />
              <EditButton hideText size="small" recordItemId={record.id} />
              <DeleteButton hideText size="small" recordItemId={record.id} />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
